// Telegram launch-params extraction — bug 2026-06-12 (Continue dead on consent).
//
// @telegram-apps/sdk-react v3 renamed the v2 API: retrieveLaunchParams() now
// returns { tgWebAppData?: InitData, ... } — there is NO `initDataRaw` field.
// Raw init data comes from the dedicated retrieveRawInitData() export. The
// old v2-shaped read (`params.initDataRaw`) silently yielded null in every
// real Telegram session, blocking POST /consent ("initData unavailable").

export interface TelegramLaunchParams {
  initDataRaw: string | null;
  languageCode: string | undefined;
  /**
   * telegram-ui `AppRoot platform` — `'ios'` для iOS-клиентов (iPhone primary),
   * `'base'` для всех остальных. Берётся из SDK `tgWebAppPlatform`, НЕ хардкод
   * (UI-SPEC §Platform Contract). Вне Telegram → `'base'`.
   */
  platform: "ios" | "base";
  /**
   * telegram-ui `AppRoot appearance` — `'light'` / `'dark'`, выводится из
   * Telegram `tgWebAppThemeParams.bg_color` (luminance). Хардкод запрещён в
   * проде (UI-SPEC §Platform Contract). Вне Telegram → `'light'`.
   */
  appearance: "light" | "dark";
}

/**
 * Маппинг Telegram `tgWebAppPlatform` → telegram-ui `AppRoot platform`.
 * telegram-ui знает только `'ios'` и `'base'`: iOS-клиент → `'ios'`, всё
 * остальное (android/tdesktop/macos/web/…) → `'base'`.
 */
function resolveAppRootPlatform(tgPlatform: string | undefined): "ios" | "base" {
  return tgPlatform === "ios" ? "ios" : "base";
}

/**
 * Выводит light/dark из `bg_color` темы Telegram через относительную яркость
 * (sRGB luminance). Тёмный фон (luminance < 0.5) → `'dark'`. Если bg_color
 * недоступен (вне Telegram / отсутствует) → `'light'` по умолчанию.
 */
function resolveAppRootAppearance(bgColor: string | undefined): "light" | "dark" {
  if (!bgColor) return "light";
  const hex = bgColor.replace("#", "");
  if (hex.length !== 6) return "light";
  const r = Number.parseInt(hex.slice(0, 2), 16);
  const g = Number.parseInt(hex.slice(2, 4), 16);
  const b = Number.parseInt(hex.slice(4, 6), 16);
  if ([r, g, b].some((c) => Number.isNaN(c))) return "light";
  // Relative luminance (sRGB, 0..255 scale): perceived brightness.
  const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  return luminance < 0.5 ? "dark" : "light";
}

/**
 * Best-effort read of raw initData + user.language_code via
 * @telegram-apps/sdk-react (v3 API).
 *
 * Outside Telegram WebView (local dev / vitest jsdom), these calls throw or
 * return undefined — handled gracefully via try/catch.
 */
export async function readTelegramLaunchParams(): Promise<TelegramLaunchParams> {
  try {
    const sdk = await import("@telegram-apps/sdk-react");
    // init() is idempotent; call once before any other SDK function.
    if (typeof sdk.init === "function") {
      sdk.init();
    }

    let initDataRaw: string | null = null;
    let languageCode: string | undefined;
    let platform: "ios" | "base" = "base";
    let appearance: "light" | "dark" = "light";

    try {
      if (typeof sdk.retrieveRawInitData === "function") {
        initDataRaw = sdk.retrieveRawInitData() ?? null;
      }
    } catch {
      // No init data — outside Telegram. OK for dev.
    }

    try {
      if (typeof sdk.retrieveLaunchParams === "function") {
        const params = sdk.retrieveLaunchParams() as {
          tgWebAppData?: { user?: { language_code?: string } };
          tgWebAppPlatform?: string;
          tgWebAppThemeParams?: { bg_color?: string };
        };
        languageCode = params.tgWebAppData?.user?.language_code;
        platform = resolveAppRootPlatform(params.tgWebAppPlatform);
        appearance = resolveAppRootAppearance(params.tgWebAppThemeParams?.bg_color);
      }
    } catch {
      // No launchParams — outside Telegram. OK for dev (defaults base/light).
    }

    return { initDataRaw, languageCode, platform, appearance };
  } catch {
    return { initDataRaw: null, languageCode: undefined, platform: "base", appearance: "light" };
  }
}
